import React, {useMemo} from 'react'
import PropTypes from 'prop-types'
import {Link as RouterLink} from 'react-router-dom'
import {Box, Flex, Image, Text} from '@chakra-ui/react'

import DisplayPrice from '../../../../display-price'
import {getPriceData} from '../../../../../utils/product-utils'
import {useGetLinkData} from '../../../../../contexts/category-url-context'
import styles from './style'

/* Build the product PDP href. Reuses `useGetLinkData` so resolution mirrors
 * what `resolveGDILink` does for single-product CTAs. */
const useProductHref = () => {
    const getLinkData = useGetLinkData()
    return (productId) => {
        if (!productId) return '#'
        const linkData = getLinkData?.({
            __typename: 'ProductLinkBlock',
            ProductId: productId
        })
        return linkData?.generatedURL || `/product/${productId}`
    }
}

/**
 * Horizontal product row — image on the left, name + price stacked on the
 * right. The whole row is a router link to the PDP. Used inside compact
 * surfaces (e.g. CTA products popover) where a vertical product tile would
 * be too tall.
 */
const HorizontalProductItem = ({product}) => {
    const buildHref = useProductHref()
    const image = product?.imageGroups?.[0]?.images?.[0]
    const imageUrl = image?.disBaseLink || image?.link
    const priceData = useMemo(() => (product ? getPriceData(product) : null), [product])
    const baseStyle = styles.baseStyle

    return (
        <Flex
            as={RouterLink}
            to={buildHref(product.id)}
            {...baseStyle.row}
        >
            <Box {...baseStyle.imageContainer}>
                {imageUrl ? (
                    <Image
                        src={imageUrl}
                        alt={image?.alt || product.name}
                        {...baseStyle.image}
                    />
                ) : null}
            </Box>
            <Flex {...baseStyle.content}>
                <Text {...baseStyle.name} noOfLines={2}>
                    {product.name}
                </Text>
                {priceData && (
                    <Flex {...baseStyle.priceRow}>
                        <DisplayPrice
                            priceData={priceData}
                            currency={product.currency}
                        />
                    </Flex>
                )}
            </Flex>
        </Flex>
    )
}

HorizontalProductItem.propTypes = {
    product: PropTypes.object.isRequired
}

export default HorizontalProductItem
